/** Variables del Worker: `vars` de wrangler.jsonc + secretos (en local, .env). */
export interface Env {
  ASSETS?: Fetcher;
  // Workers AI: traductor m2m100 gratuito (10.000 neurons/día; ~2 por pregunta)
  AI?: Pick<Ai, "run">;
  // D1: búsquedas, papers y clasificaciones (#11)
  DB?: D1Database;
  PAPERJEV_BACKEND?: string;
  TYPESAFE_API_KEY?: string;
  TYPESAFE_URL?: string;
  JEV_MODEL?: string;
  // Si está definida, la API exige la cabecera `x-access-key` (protege la clave de Jev hasta #14)
  ACCESS_KEY?: string;
  TRANSLATE?: string;
  TRANSLATOR_URL?: string;
  // Sube el límite gratuito de MyMemory de ~5.000 a ~50.000 caracteres/día
  MYMEMORY_EMAIL?: string;
  EUROPEPMC_URL?: string;
  // OpenAlex exige clave desde 2026 (1 $/día gratis; sin clave, 0,10 $/día). Cada página ≈ 0,001 $
  OPENALEX_API_KEY?: string;
  // Gasto diario máximo con la clave de OpenAlex (1 $/día gratis); después, acceso sin clave
  OPENALEX_DAILY_USD?: string;
  // Clave gratuita de NCBI (E-utilities): 10 peticiones/s en lugar de 3
  NCBI_API_KEY?: string;
  OPENALEX_URL?: string;
  // Sin clave Semantic Scholar no se usa (su cupo compartido suele dar 429)
  SEMANTIC_SCHOLAR_API_KEY?: string;
  SEMANTIC_SCHOLAR_URL?: string;
  ARXIV_URL?: string;
  EXHAUSTIVE_MAX?: string;
  // Días que se reutiliza una búsqueda guardada de la misma pregunta (caché, #12)
  CACHE_TTL_DAYS?: string;
  // Tope de gasto en Jev (#13): euros al mes, cambio y precio por millón de tokens de entrada
  BUDGET_MONTHLY_EUR?: string;
  USD_PER_EUR?: string;
  JEV_USD_PER_M_INPUT?: string;
  // Clave del panel interno de gasto (GET /api/usage); sin ella, el panel no existe
  ADMIN_KEY?: string;
  // "true": indexar sin aprobación las preguntas que cumplen criterios estrictos (#18)
  AUTO_INDEX?: string;
  // Contacto para privacidad y peticiones de borrado (#20)
  CONTACT_EMAIL?: string;
  // Límites por IP (#14): sal secreta del hash, búsquedas nuevas y papers pagados por hora
  RATE_SALT?: string;
  LIMIT_SEARCHES_PER_HOUR?: string;
  LIMIT_PAPERS_PER_HOUR?: string;
  // Turnstile (captcha invisible de Cloudflare): clave pública y secreta del widget
  TURNSTILE_SITE_KEY?: string;
  // Dominio público (#28): lo que llegue a *.workers.dev se redirige aquí
  CANONICAL_HOST?: string;
  // Cloudflare Web Analytics (#24): token público del sitio; sin él, no se mide nada
  WEB_ANALYTICS_TOKEN?: string;
  TURNSTILE_SECRET?: string;
  // "false" desactiva la ampliación de la consulta con sinónimos (MeSH, Wikidata + Jev)
  EXPAND_QUERY?: string;
  EUTILS_URL?: string;
  WIKIDATA_URL?: string;
}

export interface Settings {
  backend: "jev" | "fake";
  typesafeApiKey: string;
  typesafeUrl: string;
  jevModel: string;
  accessKey: string;
  translate: boolean;
  translatorUrl: string;
  mymemoryEmail: string;
  europepmcUrl: string;
  openalexUrl: string;
  openalexApiKey: string;
  semanticScholarUrl: string;
  semanticScholarApiKey: string;
  arxivUrl: string;
  expandQuery: boolean;
  eutilsUrl: string;
  wikidataUrl: string;
  defaultLimit: number;
  maxLimit: number;
  // Tope de la búsqueda exhaustiva: ~800 tokens por paper → 1.000 papers ≈ 3 céntimos de Jev
  exhaustiveMax: number;
  cacheTtlDays: number;
  openalexDailyUsd: number;
  ncbiApiKey: string;
  budgetMonthlyEur: number;
  usdPerEur: number;
  jevUsdPerMillionInput: number;
  adminKey: string;
  autoIndex: boolean;
  contactEmail: string;
  rateSalt: string;
  limitSearchesPerHour: number;
  limitPapersPerHour: number;
  turnstileSiteKey: string;
  canonicalHost: string;
  webAnalyticsToken: string;
  turnstileSecret: string;
  // Papers por invocación de /api/classify: 1 llamada a Jev por paper, con margen para
  // reintentos dentro del límite de 50 peticiones externas del plan gratuito. Medido en
  // producción: con 10, CPU p50 5 ms y p95 8 ms (límite gratuito: 10 ms); con 15, p95 13 ms
  batchSize: number;
  // Resultados por página de Europe PMC: acotado para no pasar de 10 ms de CPU al parsear
  pageSize: number;
}

export function getSettings(env: Env): Settings {
  return {
    backend: env.PAPERJEV_BACKEND === "jev" ? "jev" : "fake",
    typesafeApiKey: env.TYPESAFE_API_KEY?.trim() ?? "",
    typesafeUrl: env.TYPESAFE_URL ?? "https://api.typesafe.ai/v1/systemone",
    jevModel: env.JEV_MODEL ?? "jev-latest",
    accessKey: env.ACCESS_KEY?.trim() ?? "",
    translate: env.TRANSLATE !== "false",
    translatorUrl: env.TRANSLATOR_URL ?? "https://api.mymemory.translated.net/get",
    mymemoryEmail: env.MYMEMORY_EMAIL?.trim() ?? "",
    europepmcUrl: env.EUROPEPMC_URL ?? "https://www.ebi.ac.uk/europepmc/webservices/rest/search",
    openalexUrl: env.OPENALEX_URL ?? "https://api.openalex.org/works",
    openalexApiKey: env.OPENALEX_API_KEY?.trim() ?? "",
    semanticScholarUrl: env.SEMANTIC_SCHOLAR_URL ?? "https://api.semanticscholar.org/graph/v1/paper/search",
    semanticScholarApiKey: env.SEMANTIC_SCHOLAR_API_KEY?.trim() ?? "",
    arxivUrl: env.ARXIV_URL ?? "https://export.arxiv.org/api/query",
    expandQuery: env.EXPAND_QUERY !== "false",
    eutilsUrl: env.EUTILS_URL ?? "https://eutils.ncbi.nlm.nih.gov/entrez/eutils",
    wikidataUrl: env.WIKIDATA_URL ?? "https://www.wikidata.org/w/api.php",
    defaultLimit: 100,
    maxLimit: 200,
    exhaustiveMax: Number(env.EXHAUSTIVE_MAX) || 1000,
    cacheTtlDays: Number(env.CACHE_TTL_DAYS) || 30,
    // Un poco por debajo de 1 $: el tope real lo pone OpenAlex y así no se llega a su 429
    openalexDailyUsd: Number(env.OPENALEX_DAILY_USD) || 0.95,
    ncbiApiKey: env.NCBI_API_KEY?.trim() ?? "",
    budgetMonthlyEur: Number(env.BUDGET_MONTHLY_EUR) || 5,
    usdPerEur: Number(env.USD_PER_EUR) || 1.1,
    // Precio publicado de Jev: 0,042 $/M de entrada; la salida es gratis
    jevUsdPerMillionInput: Number(env.JEV_USD_PER_M_INPUT) || 0.042,
    adminKey: env.ADMIN_KEY?.trim() ?? "",
    autoIndex: env.AUTO_INDEX === "true",
    contactEmail: env.CONTACT_EMAIL?.trim() ?? "",
    rateSalt: env.RATE_SALT?.trim() ?? "",
    // 10 búsquedas nuevas (≈ 1.000 papers) y 2 exhaustivas (≈ 1.800) por hora
    limitSearchesPerHour: Number(env.LIMIT_SEARCHES_PER_HOUR) || 10,
    limitPapersPerHour: Number(env.LIMIT_PAPERS_PER_HOUR) || 3000,
    turnstileSiteKey: env.TURNSTILE_SITE_KEY?.trim() ?? "",
    canonicalHost: env.CANONICAL_HOST?.trim() ?? "",
    webAnalyticsToken: env.WEB_ANALYTICS_TOKEN?.trim() ?? "",
    turnstileSecret: env.TURNSTILE_SECRET?.trim() ?? "",
    batchSize: 10,
    pageSize: 25,
  };
}

export type FetchFn = typeof fetch;

/** Pausas entre reintentos; los tests la sustituyen para no esperar. */
export const timing = {
  sleep: (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)),
};
